import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CountrySelect } from "@/components/CountryFields";
import { Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_escuela-reports/artifacts/vmevlka0_Screenshot%202026-03-11%20at%206.06.04%E2%80%AFPM.png";

const HOW_OPTIONS = [
  "online_search", "facebook_group", "newspaper_ads", "google_ads", "facebook_ads",
  "recommendation", "street_signal", "banner", "flyers", "walk_in", "another_school", "webpage_ads",
];

const LANG_PRESETS = [
  { value: "spanish", key: "lang_spanish" },
  { value: "english", key: "lang_english" },
  { value: "french", key: "lang_french" },
  { value: "portuguese", key: "lang_portuguese" },
  { value: "german", key: "lang_german" },
  { value: "italian", key: "lang_italian" },
];

function LanguagePicker({ value, onChange, t }) {
  const [extra, setExtra] = useState("");
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const addExtra = () => {
    const v = extra.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setExtra("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {LANG_PRESETS.map((l) => {
          const active = value.includes(l.value);
          return (
            <button
              key={l.value}
              type="button"
              data-testid={`lang-chip-${l.value}`}
              onClick={() => toggle(l.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? "bg-brand-blue text-white border-brand-blue"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {t(l.key)}
            </button>
          );
        })}
        {value
          .filter((v) => !LANG_PRESETS.find((l) => l.value === v))
          .map((v) => (
            <span key={v} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
              {v}
              <button type="button" onClick={() => toggle(v)} className="hover:text-red-700">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
      </div>
      <div className="flex gap-2">
        <Input
          type="text"
          data-testid="lang-extra-input"
          placeholder={t("lang_other")}
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
        />
        <Button type="button" variant="outline" onClick={addExtra} data-testid="lang-extra-add">
          +
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">{t("field_languages_spoken_hint")}</p>
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const isTeacher = user?.role === "teacher";

  const [form, setForm] = useState({
    name: user?.name || "",
    whatsapp: user?.whatsapp || "",
    country: user?.country || "",
    date_of_birth: "",
    country_of_residence: user?.country || "",
    city: "",
    occupation: "",
    native_language: "",
    how_did_you_hear: "",
    learning_goal: "",
    has_learning_difficulty: false,
    learning_difficulty_detail: "",
    address: "",
    profession: "",
    languages_spoken: [],
    emergency_contact_name: "",
    emergency_contact_phone: "",
    meet_link: "",
    zoom_link: "",
    accept_policies: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    if (!form.accept_policies) return false;
    // Base contact fields are required for both roles
    if (!form.name.trim() || !form.whatsapp.trim() || !form.country) return false;
    if (isTeacher) {
      return (
        form.date_of_birth &&
        form.country_of_residence &&
        form.profession.trim() &&
        form.languages_spoken.length > 0
      );
    }
    // student
    return (
      form.date_of_birth &&
      form.country_of_residence &&
      form.city.trim() &&
      form.native_language.trim() &&
      form.learning_goal.trim()
    );
  };

  const submit = async () => {
    if (!validate()) {
      toast.error(t("onboarding_required_hint"));
      return;
    }
    setSubmitting(true);
    try {
      const url = isTeacher ? "/users/me/onboarding/teacher" : "/users/me/onboarding/student";
      const base = {
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim(),
        country: form.country,
      };
      const payload = isTeacher
        ? {
            ...base,
            date_of_birth: form.date_of_birth,
            country_of_residence: form.country_of_residence,
            profession: form.profession.trim(),
            languages_spoken: form.languages_spoken,
            address: form.address.trim() || null,
            emergency_contact_name: form.emergency_contact_name.trim() || null,
            emergency_contact_phone: form.emergency_contact_phone.trim() || null,
            meet_link: form.meet_link.trim() || null,
            zoom_link: form.zoom_link.trim() || null,
            accept_policies: !!form.accept_policies,
          }
        : {
            ...base,
            date_of_birth: form.date_of_birth,
            country_of_residence: form.country_of_residence,
            city: form.city.trim(),
            native_language: form.native_language.trim(),
            learning_goal: form.learning_goal.trim(),
            occupation: form.occupation.trim() || null,
            how_did_you_hear: form.how_did_you_hear || null,
            has_learning_difficulty: !!form.has_learning_difficulty,
            learning_difficulty_detail: form.has_learning_difficulty
              ? (form.learning_difficulty_detail.trim() || null)
              : null,
            accept_policies: !!form.accept_policies,
          };
      await api.patch(url, payload);
      await refresh();
      toast.success(t("onboarding_success"));
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 px-4 py-8" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <img src={LOGO_URL} alt="We Speak Spanish" className="h-10" />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
              data-testid="onboarding-lang-toggle"
            >
              🌐 {lang === "es" ? "EN" : "ES"}
            </button>
            <button
              onClick={logout}
              className="text-xs text-slate-500 hover:text-red-600 underline-offset-2 hover:underline"
              data-testid="onboarding-logout-btn"
            >
              {t("logout")}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            {isTeacher ? t("onboarding_title_teacher") : t("onboarding_title_student")}
          </h1>
          <p className="text-sm text-slate-600 mt-2">{t("onboarding_subtitle")}</p>
          <p className="text-xs text-slate-400 mt-1">{t("onboarding_required_hint")}</p>

          <div className="mt-6 space-y-5">
            {/* Base contact fields — always shown so the student can complete/edit
                anything the admin left blank at invite time. */}
            <div>
              <Label className="text-sm">{t("name")} *</Label>
              <Input
                type="text"
                data-testid="ob-name-input"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">{t("whatsapp")} *</Label>
              <Input
                type="tel"
                placeholder="+506 8888 8888"
                data-testid="ob-whatsapp-input"
                value={form.whatsapp}
                onChange={(e) => update({ whatsapp: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm">{t("country")} *</Label>
              <CountrySelect
                testId="ob-nationality-select"
                value={form.country}
                onChange={(v) => update({ country: v })}
              />
            </div>

            {/* Common: date of birth */}
            <div>
              <Label className="text-sm">{t("field_date_of_birth")} *</Label>
              <Input
                type="date"
                data-testid="ob-dob-input"
                value={form.date_of_birth}
                onChange={(e) => update({ date_of_birth: e.target.value })}
                className="mt-1.5"
              />
            </div>

            {/* Common: country */}
            <div>
              <Label className="text-sm">{t("field_country_of_residence")} *</Label>
              <CountrySelect
                testId="ob-country-select"
                value={form.country_of_residence}
                onChange={(v) => update({ country_of_residence: v })}
              />
            </div>

            {isTeacher ? (
              <>
                <div>
                  <Label className="text-sm">{t("field_address")}</Label>
                  <Input
                    type="text"
                    data-testid="ob-address-input"
                    value={form.address}
                    onChange={(e) => update({ address: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t("field_profession")} *</Label>
                  <Input
                    type="text"
                    data-testid="ob-profession-input"
                    value={form.profession}
                    onChange={(e) => update({ profession: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t("field_languages_spoken")} *</Label>
                  <div className="mt-1.5">
                    <LanguagePicker
                      value={form.languages_spoken}
                      onChange={(v) => update({ languages_spoken: v })}
                      t={t}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t("field_emergency_contact_name")}</Label>
                    <Input
                      type="text"
                      data-testid="ob-ec-name-input"
                      value={form.emergency_contact_name}
                      onChange={(e) => update({ emergency_contact_name: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t("field_emergency_contact_phone")}</Label>
                    <Input
                      type="tel"
                      data-testid="ob-ec-phone-input"
                      value={form.emergency_contact_phone}
                      onChange={(e) => update({ emergency_contact_phone: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">{t("field_meet_link")}</Label>
                  <Input
                    type="url"
                    placeholder="https://meet.google.com/..."
                    data-testid="ob-meet-link-input"
                    value={form.meet_link}
                    onChange={(e) => update({ meet_link: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t("field_meet_link_hint")}</p>
                </div>
                <div>
                  <Label className="text-sm">{t("field_zoom_link")}</Label>
                  <Input
                    type="url"
                    placeholder="https://us02web.zoom.us/j/..."
                    data-testid="ob-zoom-link-input"
                    value={form.zoom_link}
                    onChange={(e) => update({ zoom_link: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t("field_zoom_link_hint")}</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-sm">{t("field_city")} *</Label>
                  <Input
                    type="text"
                    data-testid="ob-city-input"
                    value={form.city}
                    onChange={(e) => update({ city: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t("field_occupation")}</Label>
                  <Input
                    type="text"
                    data-testid="ob-occupation-input"
                    value={form.occupation}
                    onChange={(e) => update({ occupation: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t("field_native_language")} *</Label>
                  <Input
                    type="text"
                    data-testid="ob-native-language-input"
                    value={form.native_language}
                    onChange={(e) => update({ native_language: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t("field_how_did_you_hear")}</Label>
                  <Select
                    value={form.how_did_you_hear || "__none__"}
                    onValueChange={(v) => update({ how_did_you_hear: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="ob-how-select" className="mt-1.5">
                      <SelectValue placeholder={t("pick_option")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("pick_option")}</SelectItem>
                      {HOW_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{t(`how_${o}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{t("field_learning_goal")} *</Label>
                  <Textarea
                    data-testid="ob-goal-input"
                    rows={3}
                    value={form.learning_goal}
                    onChange={(e) => update({ learning_goal: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
                  <Label className="text-sm flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.has_learning_difficulty}
                      onCheckedChange={(v) => update({ has_learning_difficulty: !!v })}
                      data-testid="ob-difficulty-checkbox"
                    />
                    <span>{t("field_has_learning_difficulty")}</span>
                  </Label>
                  {form.has_learning_difficulty && (
                    <Textarea
                      data-testid="ob-difficulty-detail-input"
                      rows={2}
                      placeholder={t("field_learning_difficulty_detail")}
                      value={form.learning_difficulty_detail}
                      onChange={(e) => update({ learning_difficulty_detail: e.target.value })}
                      className="mt-2"
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Policies & Conditions acceptance (mandatory) */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              form.accept_policies
                ? "border-emerald-300 bg-emerald-50"
                : "border-amber-300 bg-amber-50"
            }`}
            data-testid="policies-acceptance-block"
          >
            <Label className="text-sm flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.accept_policies}
                onCheckedChange={(v) => update({ accept_policies: !!v })}
                data-testid="policies-accept-checkbox"
                className="mt-0.5"
              />
              <span className="leading-relaxed">
                {t("policies_accept_prefix")}{" "}
                <a
                  href="https://wespeakspanish-cr.com/about-us/policies-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-blue font-semibold underline underline-offset-2 hover:text-blue-800"
                  data-testid="policies-link"
                >
                  {t("policies_link_text")}
                </a>{" "}
                {t("policies_accept_suffix")} <span className="text-red-600">*</span>
              </span>
            </Label>
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              onClick={submit}
              disabled={submitting || !validate()}
              data-testid="onboarding-submit-btn"
              className="btn-brand"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t("onboarding_saving")}</>
              ) : (
                <><CheckCircle2 className="h-4 w-4" /> {t("onboarding_submit")}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
